import type { TrainingPlacement } from "@lemmonhq/knowledgebase";

export type Rect = { top: number; left: number; width: number; height: number };

export type SidePlacement = "top" | "bottom" | "left" | "right";

export const PADDING = 8;
export const TOOLTIP_WIDTH = 320;
export const TOOLTIP_GAP = 12;
export const VIEWPORT_MARGIN = 12;

// Does the tooltip fully fit on `side` of `rect` without spilling past the
// viewport margin? Used so a hardcoded `placement` hint is only honored when it
// actually has room — otherwise the tooltip gets clamped back over the target.
export const placementFits = (
  side: SidePlacement,
  rect: Rect,
  vw: number,
  vh: number,
  tooltipH: number,
): boolean => {
  if (side === "bottom") {
    return vh - (rect.top + rect.height) >= tooltipH + TOOLTIP_GAP + VIEWPORT_MARGIN;
  }
  if (side === "top") {
    return rect.top >= tooltipH + TOOLTIP_GAP + VIEWPORT_MARGIN;
  }
  if (side === "right") {
    return vw - (rect.left + rect.width) >= TOOLTIP_WIDTH + TOOLTIP_GAP + VIEWPORT_MARGIN;
  }
  return rect.left >= TOOLTIP_WIDTH + TOOLTIP_GAP + VIEWPORT_MARGIN;
};

export const pickPlacement = (
  rect: Rect | null,
  preferred: TrainingPlacement | undefined,
  vw: number,
  vh: number,
  tooltipH: number,
): SidePlacement | "center" => {
  if (!rect) return "center";
  // Honor the authored placement only when the tooltip actually fits there.
  if (preferred && preferred !== "auto" && placementFits(preferred, rect, vw, vh, tooltipH)) {
    return preferred;
  }
  const spaces: [SidePlacement, number][] = [
    ["bottom", vh - (rect.top + rect.height)],
    ["top", rect.top],
    ["right", vw - (rect.left + rect.width)],
    ["left", rect.left],
  ];
  // Prefer a side the tooltip fits in; among those (or all, if none fit) take
  // the one with the most room so the overlap guard rarely has to kick in.
  const fitting = spaces.filter(([side]) => placementFits(side, rect, vw, vh, tooltipH));
  const pool = fitting.length ? fitting : spaces;
  pool.sort((a, b) => b[1] - a[1]);
  return pool[0][0];
};

export const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.left < b.left + b.width &&
  a.left + a.width > b.left &&
  a.top < b.top + b.height &&
  a.top + a.height > b.top;

export const computeTooltipPos = (
  rect: Rect | null,
  placement: ReturnType<typeof pickPlacement>,
  vw: number,
  vh: number,
  tooltipH: number,
) => {
  const centered = {
    top: Math.max(VIEWPORT_MARGIN, vh / 2 - tooltipH / 2),
    left: Math.max(VIEWPORT_MARGIN, vw / 2 - TOOLTIP_WIDTH / 2),
  };
  if (!rect || placement === "center") {
    return centered;
  }

  let top = 0;
  let left = 0;
  if (placement === "bottom") {
    top = rect.top + rect.height + TOOLTIP_GAP;
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  } else if (placement === "top") {
    top = rect.top - TOOLTIP_GAP - tooltipH;
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  } else if (placement === "right") {
    top = rect.top + rect.height / 2 - tooltipH / 2;
    left = rect.left + rect.width + TOOLTIP_GAP;
  } else {
    top = rect.top + rect.height / 2 - tooltipH / 2;
    left = rect.left - TOOLTIP_GAP - TOOLTIP_WIDTH;
  }

  const pos = {
    top: Math.max(VIEWPORT_MARGIN, Math.min(top, vh - tooltipH - VIEWPORT_MARGIN)),
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN)),
  };

  // Safety net: if clamping pulled the card back over the highlighted target,
  // drop it to centre rather than covering the spotlight (the user must always
  // see the zone they're being pointed at).
  const tooltipBox: Rect = { top: pos.top, left: pos.left, width: TOOLTIP_WIDTH, height: tooltipH };
  const padded: Rect = {
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };
  if (rectsIntersect(tooltipBox, padded)) {
    return centered;
  }
  return pos;
};
